/**
 * 十神计算引擎
 * 根据日主天干与其他天干的五行生克关系及阴阳关系，推算十神
 * 算法严谨，各维度数据一致
 */

import {
  STEMS, BRANCHES,
  type Stem, type Branch, type Element,
  STEM_ELEMENT, BRANCH_HIDDEN,
  type FourPillars,
} from '../../baziCalc';
import {
  ELEMENT_GENERATES,
  ELEMENT_OVERCOMES,
  ELEMENT_COLORS,
} from '@/utils/calcTools/unifiedRules';

// 重新导出 ELEMENT_COLORS，保持向后兼容
export { ELEMENT_COLORS };

// ── 十神类型 ──────────────────────────────────────────────────
export type Shishen =
  | '比肩' | '劫财'
  | '食神' | '伤官'
  | '偏财' | '正财'
  | '七杀' | '正官'
  | '偏印' | '正印'
  | '日主';

// ── 天干阴阳 ─────────────────────────────────────────────────
const STEM_YIN_YANG: Record<Stem, 'yang' | 'yin'> = {
  甲: 'yang', 丙: 'yang', 戊: 'yang', 庚: 'yang', 壬: 'yang',
  乙: 'yin',  丁: 'yin',  己: 'yin',  辛: 'yin',  癸: 'yin',
};

// ── 十神对应图标 ─────────────────────────────────────────────
export const SHISHEN_ICONS: Record<Shishen | '日主', string> = {
  比肩: 'ri-user-line',
  劫财: 'ri-team-line',
  食神: 'ri-lightbulb-line',
  伤官: 'ri-fire-line',
  偏财: 'ri-treasure-map-line',
  正财: 'ri-money-cny-circle-line',
  七杀: 'ri-sword-line',
  正官: 'ri-scales-line',
  偏印: 'ri-book-open-line',
  正印: 'ri-award-line',
  日主: 'ri-focus-2-line',
};

// ── 十神吉凶倾向 ─────────────────────────────────────────────
export const SHISHEN_NATURE: Record<Shishen, 'ji' | 'zhong' | 'xiong'> = {
  比肩: 'zhong',
  劫财: 'zhong',
  食神: 'ji',
  伤官: 'zhong',
  偏财: 'ji',
  正财: 'ji',
  七杀: 'zhong',
  正官: 'ji',
  偏印: 'zhong',
  正印: 'ji',
  日主: 'zhong',
};

/**
 * 计算某天干相对于日主的十神
 * @param dayStem 日主天干
 * @param otherStem 被推算的天干
 */
export function getShishen(dayStem: Stem, otherStem: Stem): Shishen {
  if (dayStem === otherStem) return '比肩';

  const dayEl = STEM_ELEMENT[dayStem];
  const otherEl = STEM_ELEMENT[otherStem];
  const dayYY = STEM_YIN_YANG[dayStem];
  const otherYY = STEM_YIN_YANG[otherStem];
  const sameYY = dayYY === otherYY;

  // 同五行
  if (dayEl === otherEl) {
    return sameYY ? '比肩' : '劫财';
  }

  // 日主所生（我生）
  if (ELEMENT_GENERATES[dayEl] === otherEl) {
    return sameYY ? '食神' : '伤官';
  }

  // 日主所克（我克）
  if (ELEMENT_OVERCOMES[dayEl] === otherEl) {
    return sameYY ? '偏财' : '正财';
  }

  // 生日主（生我）
  if (ELEMENT_GENERATES[otherEl] === dayEl) {
    return sameYY ? '偏印' : '正印';
  }

  // 克日主（克我）
  if (ELEMENT_OVERCOMES[otherEl] === dayEl) {
    return sameYY ? '七杀' : '正官';
  }

  return '比肩'; // fallback（理论上不会到达此处）
}

/**
 * 获取十神对应的五行颜色（基于该十神所属的五行，而非死记）
 * 色彩语义：以对应天干的五行颜色呈现，保证与五行模块颜色一致
 */
export function getShishenColor(dayStem: Stem, targetStem: Stem): string {
  return ELEMENT_COLORS[STEM_ELEMENT[targetStem]];
}

/**
 * 十神详细描述 - 根据日主五行和十神类型给出语义化解读
 * 遵循"温和表述"原则，不利结果给出化解方向
 */
const SHISHEN_DESCRIPTIONS: Record<Shishen, {
  brief: string;
  traits: string[];
  career: string;
  emotion: string;
  health: string;
}> = {
  比肩: {
    brief: '比肩代表同类之人，个性独立，自主意识强，有协作互助之缘',
    traits: ['独立自主', '重情重义', '竞争意识强', '善于合作'],
    career: '适合需要团队协作或个人竞争的领域，贵人多来自同行同业',
    emotion: '感情中较为独立，注重平等，不甘受制于人，婚姻需相互尊重',
    health: '体质较为均衡，注意防止过度消耗自身能量。如有不适，以专业医疗建议为准',
  },
  劫财: {
    brief: '劫财个性鲜明，有魄力，善于开拓，充分发挥这份冲劲可以成就卓越',
    traits: ['魄力十足', '行事果断', '开拓进取', '竞争心强'],
    career: '适合创业、销售、竞技类领域，善于在竞争中脱颖而出。劫财为忌时宜先定规则再合作，避免利益模糊',
    emotion: '感情热烈直接，有占有欲，需培养包容与信任，减少控制欲。这不是不好，而是提醒您在关系中留给对方更多空间',
    health: '精力旺盛，但需防止用力过猛透支。八字可提示「作息、压力、脾胃、肝胆」等倾向，如有不适，以专业医疗建议为准',
  },
  食神: {
    brief: '食神主福寿，聪明乐观，才华内蕴，生活中自有一份悠然富足之气',
    traits: ['聪明机智', '乐观随和', '才华横溢', '享乐主义'],
    career: '适合艺术、餐饮、教育、创意类工作，才华得以自然流露',
    emotion: '感情细腻温和，重视生活质量，伴侣需欣赏其才华与品味',
    health: '消化系统较好，需防止贪图安逸导致身体懒散。如有不适，以专业医疗建议为准',
  },
  伤官: {
    brief: '伤官聪颖独特，创新力强，个性不拘一格，勇于突破规则——这是时代进步的动力',
    traits: ['聪颖独特', '创新突破', '表达力强', '个性鲜明'],
    career: '适合需要创意、表达与突破的领域，如媒体、艺术、法律、研究。伤官见官之年宜少说多做，印星当令年是最佳发力窗口',
    emotion: '感情丰富且理想化，对伴侣要求较高，需学会适度包容与妥协。这不是短板，而是提醒您欣赏现实中的不完美',
    health: '神经较敏感，注意舒缓压力，避免思虑过重影响睡眠。八字可提示生活调节倾向，如有不适，以专业医疗建议为准',
  },
  偏财: {
    brief: '偏财代表意外之财与广泛人缘，善于在人际网络中创造机会',
    traits: ['八面玲珑', '善于社交', '财运灵活', '慷慨大方'],
    career: '适合商贸、金融、销售、公关等流动性强的领域',
    emotion: '感情多元，桃花运旺，需专情才能获得稳定幸福',
    health: '注意饮食规律，防止饮食不节制影响肝脾。如有不适，以专业医疗建议为准',
  },
  正财: {
    brief: '正财脚踏实地，诚实守信，理财有道，财富通过努力稳健积累',
    traits: ['务实稳健', '诚信守约', '理财有道', '重视物质基础'],
    career: '适合金融、会计、行政、实业等需要务实积累的领域',
    emotion: '感情稳重专一，重视家庭与物质基础，婚姻踏实长久',
    health: '整体健康，注意脾胃调理，规律饮食是关键。如有不适，以专业医疗建议为准',
  },
  七杀: {
    brief: '七杀代表挑战与压力，能激发潜能，七杀有制则为权——这是命局中最强劲的成长催化剂',
    traits: ['魄力过人', '勇于挑战', '意志坚定', '领导力强'],
    career: '适合军警、管理、竞技性强的领域，压力下能激发最大潜能。七杀为忌时宜降低冒进、建立流程和边界，用成果说话而非硬刚',
    emotion: '感情强烈，有保护欲，但需防止关系中权力失衡，多沟通包容。这不是缺陷，而是提醒您以守护代替管控',
    health: '精力充沛，但需防止过度劳累和情绪激动。八字可提示「心火、脾胃、肝胆」等倾向，如有不适，以专业医疗建议为准',
  },
  正官: {
    brief: '正官代表规则与荣誉，自律守正，社会认可度高，事业稳步上升',
    traits: ['自律守正', '责任感强', '处事公正', '重视名誉'],
    career: '适合政府机关、企业管理、教育、法律等需要公信力的领域',
    emotion: '感情传统稳重，忠诚专一，重视婚姻的社会认可与门当户对',
    health: '整体平稳，注意防止过度压抑情绪，适当释放压力。如有不适，以专业医疗建议为准',
  },
  偏印: {
    brief: '偏印洞察力强，学识深厚，思维独特，善于钻研与独立思考',
    traits: ['洞察力强', '思维独特', '博学多才', '独立内省'],
    career: '适合研究、哲学、宗教、技术、咨询等需要深度思考的领域',
    emotion: '感情较为内敛，不善主动表达，需对方主动理解与接纳',
    health: '精神能量消耗较大，需注意休息，避免过度内耗。如有不适，以专业医疗建议为准',
  },
  正印: {
    brief: '正印代表贵人与庇护，得长辈眷顾，学识广博，心性善良',
    traits: ['善良仁厚', '学识渊博', '贵人运旺', '心思缜密'],
    career: '适合教育、文化、医疗、公益等需要仁心与学识的领域',
    emotion: '感情温柔细腻，重视精神契合，宜找成熟稳重的伴侣',
    health: '整体健康，注意过度依赖他人可能带来的心理压力。如有不适，以专业医疗建议为准',
  },
  日主: {
    brief: '日主为命局核心，代表自我本性与先天禀赋，能量强弱决定行运喜忌',
    traits: ['自我意识强', '个性鲜明', '能量集中', '先天禀赋'],
    career: '日主为自身立身之本，职业方向需结合喜用神与格局综合判断',
    emotion: '日主代表感情中的自我定位，认识自己才能更好地经营关系',
    health: '日主强弱关乎整体精力水平，注意劳逸结合。如有不适，以专业医疗建议为准',
  },
};

export function getShishenDetail(shishen: Shishen) {
  return SHISHEN_DESCRIPTIONS[shishen] ?? SHISHEN_DESCRIPTIONS['比肩'];
}

// ── 十神分布计算 ─────────────────────────────────────────────

export interface ShishenCountItem {
  name: Shishen;
  count: number;
  weight: number;
  color: string;
  icon: string;
  desc: string;
}

/**
 * 计算四柱中各十神出现次数（天干+地支藏干），返回前 N 个主要十神
 * 地支藏干按主气/中气/余气权重折算（>= 0.3 计入）
 */
export function calcShishenDistribution(fourPillars: FourPillars): ShishenCountItem[] {
  const dayStem = fourPillars.day.stem;
  const counts: Partial<Record<Shishen, number>> = {};

  const pillars = [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour];

  for (let i = 0; i < pillars.length; i++) {
    const p = pillars[i];

    // 天干
    if (i !== 2) {
      // 日柱天干是日主，不计入十神
      const ss = getShishen(dayStem, p.stem);
      counts[ss] = (counts[ss] ?? 0) + 1;
    }

    // 地支藏干（不再忽略低权重藏干，保留全部含余气）
    for (const hidden of BRANCH_HIDDEN[p.branch]) {
      const ss = getShishen(dayStem, hidden.stem);
      counts[ss] = (counts[ss] ?? 0) + hidden.weight;
    }
  }

  return (Object.entries(counts) as [Shishen, number][])
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([name, rawCount]) => {
      const weight = Math.round(rawCount * 10) / 10;
      const count = Math.round(rawCount);
      // weight 保留一位小数用于精细化展示，count 为整数用于 UI
      // 找到第一个对应该十神的天干来确定颜色
      const representativeStem = STEMS.find(s => getShishen(dayStem, s) === name);
      const color = representativeStem ? ELEMENT_COLORS[STEM_ELEMENT[representativeStem]] : '#8A7B91';
      const detail = getShishenDetail(name);
      return {
        name,
        count,
        weight,
        color,
        icon: SHISHEN_ICONS[name],
        desc: detail.brief,
      };
    });
}

/**
 * 获取四柱中天干对应的十神名（用于排盘显示）
 */
export function getStemShishen(dayStem: Stem, stem: Stem, isDayPillar: boolean): string {
  if (isDayPillar) return '日主';
  return getShishen(dayStem, stem);
}

/**
 * 获取地支（地支藏干主气）对应的十神名（用于排盘显示）
 */
export function getBranchMainShishen(dayStem: Stem, branch: Branch): string {
  const hidden = BRANCH_HIDDEN[branch];
  if (!hidden || hidden.length === 0) return '';
  const mainStem = hidden[0].stem; // 主气
  return getShishen(dayStem, mainStem);
}

/**
 * 获取地支藏干完整列表的十神描述（用于藏干展示）
 */
export function getBranchHiddenShishen(dayStem: Stem, branch: Branch): Array<{ stem: Stem; shishen: Shishen; weight: number }> {
  return BRANCH_HIDDEN[branch].map(h => ({
    stem: h.stem,
    shishen: getShishen(dayStem, h.stem),
    weight: h.weight,
  }));
}

// ── 四柱落位结构分析 ───────────────────────────────────────────

/** 单个柱位的十神落位 */
export interface ShishenPillarSlot {
  pillar: string;
  stem: Stem;
  stemShishen: Shishen | '日主';
  branch: Branch;
  hidden: Array<{ stem: Stem; shishen: Shishen; weight: number; isMain: boolean }>;
}

/** 单个十神在四柱中的分布 */
export interface ShishenPosition {
  name: Shishen;
  color: string;
  icon: string;
  stemPositions: Array<{ pillar: string; stem: Stem }>;
  hiddenPositions: Array<{ pillar: string; branch: Branch; stem: Stem; weight: number; isMain: boolean }>;
  rootCount: number;
  totalWeight: number;
  pillarPresence: string[];
}

/** 十神四柱结构总览 */
export interface ShishenStructure {
  pillars: ShishenPillarSlot[];
  positions: ShishenPosition[];
}

function initPosition(name: Shishen, dayStem: Stem): ShishenPosition {
  const representativeStem = STEMS.find(s => getShishen(dayStem, s) === name);
  const color = representativeStem ? ELEMENT_COLORS[STEM_ELEMENT[representativeStem]] : '#8A7B91';
  return {
    name,
    color,
    icon: SHISHEN_ICONS[name],
    stemPositions: [],
    hiddenPositions: [],
    rootCount: 0,
    totalWeight: 0,
    pillarPresence: [],
  };
}

/**
 * 计算十神在四柱中的详细落位结构
 * 不再粗暴忽略低权重藏干，保留全部藏干（含余气0.1）
 * 天干透干权重=1.0，藏干按实际权重累加
 */
export function calcShishenStructure(fourPillars: FourPillars): ShishenStructure {
  const dayStem = fourPillars.day.stem;
  const pillarList = [
    { pillar: 'year', stem: fourPillars.year.stem, branch: fourPillars.year.branch },
    { pillar: 'month', stem: fourPillars.month.stem, branch: fourPillars.month.branch },
    { pillar: 'day', stem: fourPillars.day.stem, branch: fourPillars.day.branch },
    { pillar: 'hour', stem: fourPillars.hour.stem, branch: fourPillars.hour.branch },
  ];

  const pillars: ShishenPillarSlot[] = pillarList.map(p => {
    const stemShishen: Shishen | '日主' = p.pillar === 'day' ? '日主' : getShishen(dayStem, p.stem);
    const hidden = BRANCH_HIDDEN[p.branch].map(h => ({
      stem: h.stem,
      shishen: getShishen(dayStem, h.stem),
      weight: h.weight,
      isMain: h.weight >= 0.5,
    }));
    return { pillar: p.pillar, stem: p.stem, stemShishen, branch: p.branch, hidden };
  });

  const posMap = new Map<Shishen, ShishenPosition>();

  for (const p of pillars) {
    if (p.stemShishen !== '日主') {
      const ss = p.stemShishen;
      if (!posMap.has(ss)) posMap.set(ss, initPosition(ss, dayStem));
      const pos = posMap.get(ss)!;
      pos.stemPositions.push({ pillar: p.pillar, stem: p.stem });
      pos.totalWeight += 1.0;
      if (!pos.pillarPresence.includes(p.pillar)) pos.pillarPresence.push(p.pillar);
    }

    for (const h of p.hidden) {
      const ss = h.shishen;
      if (!posMap.has(ss)) posMap.set(ss, initPosition(ss, dayStem));
      const pos = posMap.get(ss)!;
      pos.hiddenPositions.push({
        pillar: p.pillar,
        branch: p.branch,
        stem: h.stem,
        weight: h.weight,
        isMain: h.isMain,
      });
      pos.totalWeight += h.weight;
      if (h.weight >= 0.3) pos.rootCount += 1;
      if (!pos.pillarPresence.includes(p.pillar)) pos.pillarPresence.push(p.pillar);
    }
  }

  const positions = Array.from(posMap.values()).sort((a, b) => b.totalWeight - a.totalWeight);

  return { pillars, positions };
}

// ── 十神四大象限分类 ─────────────────────────────────────────────

export interface ShishenQuadrant {
  leader: ShishenPosition[];    // 主导：透干 + 权重高
  support: ShishenPosition[];   // 辅助：有根不透干
  excess: ShishenPosition[];    // 失衡：权重异常偏高
  risk: ShishenPosition[];      // 风险：为忌 + 透干/在月令
}

/**
 * 将十神结构分类为四大象限
 * @param structure 十神结构
 * @param jiElements 忌神五行列表（来自喜用神计算）
 * @param dayStem 日主天干
 */
export function classifyShishenQuadrants(
  structure: ShishenStructure,
  jiElements: Element[],
  dayStem: Stem,
): ShishenQuadrant {
  const positions = [...structure.positions].sort((a, b) => b.totalWeight - a.totalWeight);
  const totalPositions = positions.length;
  if (totalPositions === 0) return { leader: [], support: [], excess: [], risk: [] };

  const avgWeight = positions.reduce((s, p) => s + p.totalWeight, 0) / totalPositions;

  const leader: ShishenPosition[] = [];
  const support: ShishenPosition[] = [];
  const excess: ShishenPosition[] = [];
  const risk: ShishenPosition[] = [];

  // 预计算：哪些十神对应的五行是忌神
  const isJiShishen = (name: Shishen): boolean => {
    const repStem = STEMS.find(s => getShishen(dayStem, s) === name);
    if (!repStem) return false;
    return jiElements.includes(STEM_ELEMENT[repStem]);
  };

  for (const pos of positions) {
    const hasTougan = pos.stemPositions.length > 0;
    const hasRoot = pos.rootCount > 0;
    const isJi = isJiShishen(pos.name);
    const inMonth = pos.pillarPresence.includes('month');
    const isExcess = pos.totalWeight > avgWeight * 1.4;

    // 风险优先（为忌 + 透干/月令）
    if (isJi && (hasTougan || inMonth)) {
      risk.push(pos);
      continue;
    }

    // 失衡（权重异常偏高，排除已进入risk的）
    if (isExcess && pos.totalWeight >= 2.0) {
      excess.push(pos);
      continue;
    }

    // 主导：透干 + 权重不低于均值90%
    if (hasTougan && pos.totalWeight >= avgWeight * 0.9) {
      leader.push(pos);
      continue;
    }

    // 辅助：有根但不透干
    if (hasRoot && !hasTougan) {
      support.push(pos);
      continue;
    }

    // 兜底
    if (pos.totalWeight >= avgWeight) {
      if (hasTougan) leader.push(pos);
      else if (hasRoot) support.push(pos);
    }
  }

  // 保底：确保leader至少有一个
  if (leader.length === 0) {
    const firstTougan = positions.find(p => p.stemPositions.length > 0);
    if (firstTougan) leader.push(firstTougan);
  }

  // 去重：一个十神只在一个象限
  const used = new Set<string>();
  const dedup = (arr: ShishenPosition[]): ShishenPosition[] => {
    const result: ShishenPosition[] = [];
    for (const p of arr) {
      if (!used.has(p.name)) {
        used.add(p.name);
        result.push(p);
      }
    }
    return result;
  };

  return {
    leader: dedup(leader),
    support: dedup(support),
    excess: dedup(excess),
    risk: dedup(risk),
  };
}

// 导出类型
export type { Stem, Branch, Element, FourPillars };
export { STEMS, BRANCHES, STEM_ELEMENT };
